import React, { useMemo, useState } from "react";
import { ProductListAction } from "../ProductList";
import { ProductListParams } from "../ProductListURLConfig";
import { useSelector } from "react-redux";
import { RootState } from "../../../utils/store";
import { Button, Loader, Popup } from "semantic-ui-react";
import { Text } from "../../../components/Text";

export type Props = {
  currentPage: number;
  currentParameters: ProductListParams;
  errorSearchQuery: boolean;
  onPageChange: (page: number) => void;
  // Only passed when the count is fetched on demand (avoid_counting_products).
  onShowCount?: () => Promise<void>;
  onProductListAction: (
    action: ProductListAction,
    value: ProductListParams
  ) => void;
  paginationSize: number;
};

export const ProductListPaginator: React.FC<Props> = ({
  currentPage,
  currentParameters,
  errorSearchQuery,
  onPageChange,
  onShowCount,
  onProductListAction,
  paginationSize = 0,
}) => {
  const { productCount, hasNext } = useSelector(
    (state: RootState) => state.productList.paginationDetails
  );
  const products = useSelector(
    (state: RootState) => state.productList.products
  );
  const [countLoading, setCountLoading] = useState(false);

  // Without onShowCount a missing count just means it is still in flight, and
  // the paginator keeps its loading state instead of offering "many".
  const countOnDemand =
    (productCount === null || productCount === undefined) &&
    Boolean(onShowCount);
  const pageLength = products?.length ?? 0;

  const totalPages = countOnDemand
    ? null
    : Math.ceil((productCount ?? 0) / paginationSize);
  const { firstOnPage, lastOnPage } = useMemo(() => {
    if (countOnDemand) {
      if (!pageLength) return { firstOnPage: 0, lastOnPage: 0 };
      const first = (currentPage - 1) * paginationSize + 1;
      return { firstOnPage: first, lastOnPage: first + pageLength - 1 };
    }
    if (!productCount) return { firstOnPage: 0, lastOnPage: 0 };
    return {
      firstOnPage: (currentPage - 1) * paginationSize + 1,
      lastOnPage: Math.min(currentPage * paginationSize, productCount),
    };
  }, [countOnDemand, pageLength, productCount, currentPage, paginationSize]);
  const isLastPage = countOnDemand ? !hasNext : totalPages === currentPage;

  const errorMessage = useMemo(() => {
    if (errorSearchQuery) return "Something went wrong";
    if (firstOnPage > lastOnPage) return "Page out of bounds";
    // `products` is null while a page loads and [] when it came back empty --
    // unlike the count, its length alone cannot tell those two apart.
    if (countOnDemand ? products?.length === 0 : productCount === 0)
      return "No Documents found";
  }, [
    errorSearchQuery,
    firstOnPage,
    lastOnPage,
    countOnDemand,
    products,
    productCount,
  ]);

  const handleShowCount = async (): Promise<void> => {
    if (!onShowCount) return;
    setCountLoading(true);
    try {
      await onShowCount();
    } finally {
      setCountLoading(false);
    }
  };

  const isLoading = countOnDemand
    ? products === null
    : !firstOnPage && !lastOnPage && !productCount;
  return (
    <>
      {errorMessage ? (
        <Text
          compact
          size="medium"
          color="grey"
          testId="product-list-paginator-error"
        >
          {errorMessage}
        </Text>
      ) : (
        <Text compact size="medium" testId="product-list-paginator-page-info">
          {isLoading ? (
            <Loader active size="tiny" inline as="span" />
          ) : countOnDemand ? (
            <>
              {firstOnPage} &ndash; {lastOnPage}{" "}
              <Button
                data-testid="product-list-paginator-show-count"
                compact
                basic
                icon="refresh"
                content="Load total count"
                loading={countLoading}
                onClick={handleShowCount}
                // A tw- class would lose to `.ui.button`'s specificity.
                style={{ fontSize: "inherit" }}
              />
            </>
          ) : (
            <>
              {firstOnPage} &ndash; {lastOnPage} of {productCount}
            </>
          )}
        </Text>
      )}
      <div>
        {errorMessage ? (
          <>
            <Popup
              content="Refresh Document list"
              size="small"
              wide
              trigger={
                <Button
                  data-testid="product-list-paginator-error-refresh"
                  compact
                  basic
                  size="tiny"
                  icon="refresh"
                  onClick={(): void => {
                    onPageChange(currentPage);
                  }}
                />
              }
            />
            <Popup
              content="Clear filters"
              size="small"
              wide
              trigger={
                <Button
                  data-testid="product-list-paginator-error-clear-filters"
                  compact
                  basic
                  size="tiny"
                  icon="eraser"
                  onClick={(): void => {
                    onProductListAction(
                      ProductListAction.Reset,
                      currentParameters
                    );
                  }}
                />
              }
            />
          </>
        ) : (
          <>
            <Button
              data-testid="product-list-paginator-prev-page"
              compact
              basic
              size="tiny"
              icon="chevron left"
              disabled={currentPage === 1}
              onClick={(): void => {
                if (currentPage !== 1) onPageChange(currentPage - 1);
              }}
            />
            <Button
              data-testid="product-list-paginator-next-page"
              compact
              basic
              size="tiny"
              icon="chevron right"
              disabled={isLastPage}
              onClick={(): void => {
                if (!isLastPage) onPageChange(currentPage + 1);
              }}
            />
          </>
        )}
      </div>
    </>
  );
};
